import type { Evidence } from '@areapif/areapif-shared'
import { InspectionEvidenceType } from '@areapif/areapif-shared'
import Icon from '@mdi/react'
import { Badge, Box, Tooltip, Typography } from '@mui/material'
import { FC } from 'react'
import { useTranslate } from 'react-admin'

import type { EvidenceStepView } from '../../domain/activeInspection'
import { getRemoteUrl } from '../../lib/supabase'
import { INSPECTION_STEP_ICON_PATHS } from './inspectionStepIcons'
import { STEP_STATUS_STYLES } from './stepStatusStyles'

/** The bucket every inspection image lives in; the same one the galleries read. */
const BUCKET = 'inspections'

const THUMBNAIL_SIZE = 56

/**
 * The tooltip of one step: its name, status and count, followed by a thumbnail
 * of every photo and every classified seal it carries.
 *
 * Mounted by the tooltip only while it is open — MUI's popper renders nothing
 * while closed — so the storage URLs are resolved, and the images requested,
 * the first time the operator rests on the step and not once per row of a
 * 200-row table. A step without evidence renders its text line alone.
 */
const EvidenceStepPreview: FC<{ step: EvidenceStepView }> = ({ step }) => {
  const translate = useTranslate()
  const stepName = translate(`resources.historical_inspections.enums.inspection_step.${step.stepId}`)
  const statusName = translate(`resources.inspections.enums.step_status.${step.status}`)

  return (
    <Box data-testid={`evidence-step-preview-${step.stepId}`}>
      <Typography variant="inherit">{`${stepName} — ${statusName} (${String(step.count)})`}</Typography>
      {step.evidence.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
          {step.evidence.map((item, index) => (
            <EvidenceThumbnail key={item.path} item={item} index={index} />
          ))}
        </Box>
      )}
    </Box>
  )
}

/**
 * Labelled the way the details galleries label the same items: a photo by its
 * position in the step, a seal by its serial number, printed under the image
 * when the seal has one.
 */
const EvidenceThumbnail: FC<{ item: Evidence; index: number }> = ({ item, index }) => {
  const translate = useTranslate()
  const isSeal = item.type === InspectionEvidenceType.SEAL
  const serialId = isSeal && item.serialId !== '' ? item.serialId : null
  const label = isSeal
    ? translate('resources.inspections.evidence_preview.seal')
    : translate('resources.inspections.evidence_preview.photo', { index: index + 1 })

  return (
    // The column is as wide as the thumbnail, or as the serial number when that
    // is wider: a serial cut short is a serial the operator cannot compare.
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: THUMBNAIL_SIZE }}>
      <Box
        component="img"
        src={getRemoteUrl(BUCKET, item.path)}
        alt={serialId === null ? label : `${label} ${serialId}`}
        loading="lazy"
        sx={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE, objectFit: 'cover', borderRadius: 1, display: 'block' }}
      />
      {serialId !== null && (
        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
          {serialId}
        </Typography>
      )}
    </Box>
  )
}

export interface EvidenceStepStripProps {
  steps: EvidenceStepView[]
  size?: number
}

/**
 * Renders the evidence state machine of one inspection as a compact strip: one
 * circle per step of its flow, coloured by step status and badged with the
 * number of evidences it carries. Hovering or focusing a circle previews the
 * step's evidence next to its name and status.
 */
export const EvidenceStepStrip: FC<EvidenceStepStripProps> = ({ steps, size = 26 }) => {
  return (
    <Box data-testid="evidence-step-strip" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {steps.map(step => {
        const style = STEP_STATUS_STYLES[step.status]

        return (
          <Tooltip
            key={step.stepId}
            title={<EvidenceStepPreview step={step} />}
            // Wide enough for a row of thumbnails; the default 300px would wrap
            // the fourth one onto a second line.
            slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
          >
            <Badge
              // Focusable so the keyboard reaches the same preview the pointer
              // does: MUI opens the tooltip on a focus-visible child.
              tabIndex={0}
              badgeContent={step.count > 1 ? step.count : 0}
              overlap="circular"
              // Deliberately monochrome: a filled badge in the theme's primary
              // blue is the same blue as an ACTIVE step, so the count read as a
              // step status of its own.
              sx={{
                borderRadius: '50%',
                '&:focus-visible': { outline: '2px solid #3b82f6', outlineOffset: 2 },
                '& .MuiBadge-badge': {
                  fontSize: '0.55rem',
                  height: 14,
                  minWidth: 14,
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  border: '1px solid #000000',
                },
              }}
            >
              <Box
                data-testid={`evidence-step-${step.stepId}`}
                data-status={step.status}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: size,
                  height: size,
                  borderRadius: '50%',
                  backgroundColor: style.backgroundColor,
                  border: `2px solid ${style.borderColor}`,
                }}
              >
                <Icon path={INSPECTION_STEP_ICON_PATHS[step.stepId]} size={`${String(size * 0.55)}px`} color={style.iconColor} />
              </Box>
            </Badge>
          </Tooltip>
        )
      })}
    </Box>
  )
}
